import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import { Autocomplete, Chip } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';

import AsyncLoader from '~components/AsyncLoader';
import { DotLoader } from '~components/DotLoader';
import EmptyState from '~components/EmptyState';
import useDebounce from '~hooks/useDebounce';
import CreateDiallerGroupModal from '~pages/CampaignManagement/DiallerGroupList/CreateDiallerGroupModal';
import useDiallerGroupSearch from '~pages/CampaignManagement/DiallerGroupList/useDiallerGroupSearch';
import { CreateDiallerGroup } from '~pages/CampaignManagement/domain';
import { useAuth } from '~providers/AuthProvider';
import { AccessScope } from '~providers/AuthProvider/domain';
import { useNotification } from '~providers/NotificationProvider';
import { useUserPreferences } from '~providers/UserPreferencesProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import GroupCard from './GroupCard';
import { DiallerGroupFilterType } from './useDiallerGroupSearch/domain';

type FilterOption = { value: DiallerGroupFilterType; label: string };
const filterOptions: FilterOption[] = [
  { value: DiallerGroupFilterType.Active, label: 'Active' },
  { value: DiallerGroupFilterType.Paused, label: 'Paused' },
  { value: DiallerGroupFilterType.Archived, label: 'Archived' },
];

const DiallerGroupList = () => {
  const { hasScope } = useAuth();
  const { accessFilter } = useUserPreferences();
  const { pushNotification } = useNotification();
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [submittingData, setSubmittingData] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>(
    filterOptions.filter((i) => i.value !== DiallerGroupFilterType.Archived),
  );
  const filters = selectedFilters.map((i) => i.value);
  const debouncedSearch = useDebounce(search, 500);
  const {
    loading,
    error,
    list,
    hasMore,
    create,
    intersectionObserverRef: lastDataElement,
  } = useDiallerGroupSearch(debouncedSearch, {
    accessFilterId: accessFilter?.id,
    filters,
  });
  const noSearchOrFilterSet = search === '' && filters.length === 0;

  const toggleCreateModal = () => {
    setCreateModalOpen((prev) => !prev);
  };

  const addDiallerGroup = async (data: CreateDiallerGroup) => {
    setSubmittingData(true);

    try {
      await create(data);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);
      return Promise.reject();
    } finally {
      setSubmittingData(false);
    }

    pushNotification('success', `Created dialler group ${data.name}`);
    setCreateModalOpen(false);
  };

  const displayList = useMemo(
    () =>
      list.map((item, index) => (
        <GroupCard key={item.id} ref={index === list.length - 1 ? lastDataElement : null} group={item} />
      )),
    [list, lastDataElement],
  );

  return (
    <>
      <Grid sx={{ marginBottom: 1 }} container spacing={1} alignContent='center'>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            variant='outlined'
            label='Search'
            id='search'
            name='search'
            defaultValue={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            fullWidth
            filterSelectedOptions
            onChange={(_, filters) => {
              setSelectedFilters(filters);
            }}
            value={selectedFilters}
            options={filterOptions}
            getOptionLabel={(option) => option.label}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  deleteIcon={<CancelIcon />}
                  label={option.label}
                  variant='filled'
                  color='primary'
                />
              ))
            }
            renderInput={(params) => <TextField {...params} label='Filters' variant='outlined' name='filters' />}
          />
        </Grid>

        <Hidden smDown>
          <Grid item md={2}></Grid>
        </Hidden>

        {hasScope(AccessScope.CanCreateGroup) && (
          <Grid style={{ display: 'flex', alignItems: 'center' }} item xs={12} md={3}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              fullWidth
              startIcon={<AddIcon />}
              onClick={toggleCreateModal}>
              Create Group
            </Button>
          </Grid>
        )}
      </Grid>

      <AsyncLoader isLoading={loading && list.length === 0}>
        <Grid container spacing={1} alignContent='center'>
          <Grid item xs={12}>
            {list.length > 0 && (
              <>
                <List>{displayList}</List>
                {loading && list.length > 0 && <DotLoader align='center' />}

                {!loading && !hasMore && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    No more results to display
                  </Typography>
                )}

                {error && list.length > 0 && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    Failed to load groups
                  </Typography>
                )}
              </>
            )}

            {list.length === 0 && !noSearchOrFilterSet && (
              <EmptyState
                type='no-records-found'
                text='No groups found matching your search criteria'
                subText='Try alternate words or selections.'
              />
            )}

            {list.length === 0 && noSearchOrFilterSet && !accessFilter && (
              <EmptyState type='no-items-3' text='No groups currently exist' />
            )}

            {list.length === 0 && noSearchOrFilterSet && accessFilter && (
              <EmptyState
                type='no-records-found'
                text={`${accessFilter.name} access filter does not have any groups assigned to it`}
              />
            )}
          </Grid>
        </Grid>

        <CreateDiallerGroupModal
          open={createModalOpen}
          submitting={submittingData}
          onClose={toggleCreateModal}
          onAccept={addDiallerGroup}
        />
      </AsyncLoader>
    </>
  );
};

export default DiallerGroupList;
